import * as Sentry from '@sentry/nextjs';

/**
 * Report every 5xx one of our own API routes hands back to a browser.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * A red toast in somebody's browser reaches nobody. `Toast.tsx` reports
 * nothing, and on the server only ~40 of ~700 API routes call Sentry at all:
 * the rest catch the fault, return `{ error }` with a 5xx, and tell no one.
 *
 * MUCKA-WEB-A7 (23 Sep 2026) is the case that asked the question. Every quote
 * with a price-list line was refused with "saveQuoteContent: Invalid quote
 * line". We saw it on CREATE only because `POST /api/quotes` had been given its
 * own capture after the 10 Sep outage; `PATCH /api/quotes/[id]` hit the same
 * bug through a catch with no report in it, so every EDIT failure was silent,
 * and so were creates for the four days before that capture existed. One
 * customer telling us was the only signal there was.
 *
 * Watching the response in the browser covers every route at once, including
 * the ones written tomorrow, without editing each catch.
 *
 * ── Why not Sentry's own `httpClientIntegration` ───────────────────────────
 *
 * It titles every event "HTTP Client Error with status code: 503" and never
 * reads the body, so the one useful thing, the server's own sentence, is
 * thrown away. And it cannot see `X-Mucka-Offline`: the service worker answers
 * an offline GET with a synthetic 504 (`offlineResponse()` in `sw.js`), so an
 * engineer working in a cellar would report every request he made.
 *
 * ── 🚨 The wrapper must not be able to change a request ────────────────────
 *
 * Vercel BotID wrapped `window.fetch` and blocked every signup for nine days
 * (see `instrumentation-client.ts`): its wrapper threw before the request left
 * the browser, and nothing reported because nothing reached a server. So this
 * one returns the ORIGINAL promise, untouched, and observes it on a detached
 * branch. Every throw in here is caught. A caller cannot tell it is installed.
 */

/** Same-origin API calls only. Third parties' 5xx are theirs to watch. */
const API_PREFIX = '/api/';

/**
 * One report per route+status per page per window. A polling screen whose
 * endpoint is down would otherwise spend the monthly quota on one fault.
 */
const DEDUPE_MS = 5 * 60_000;
const lastReported = new Map<string, number>();

/** Enough to read the fault; short enough never to carry a document. */
const BODY_MAX = 300;

/**
 * `/api/quotes/1b0e…/send` → `/api/quotes/[id]/send`.
 *
 * The route, not the path: an id in the fingerprint would make every quote its
 * own issue and split the one number worth reading, how many people hit it
 * (CLAUDE.md #13).
 */
export function routeTemplate(pathname: string): string {
  return pathname
    .split('/')
    .map(seg =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) || /^\d+$/.test(seg)
      // Share-link short ids: 12+ alphanumerics mixing letters and digits.
      // Hyphenated words (`oauth2-callback`) are route names, not ids.
      || (seg.length >= 12 && /\d/.test(seg) && /[A-Za-z]/.test(seg) && /^[A-Za-z0-9]+$/.test(seg))
        ? '[id]'
        : seg,
    )
    .join('/');
}

/** The server's own words, from `{ error }` JSON or plain text. Never throws. */
export async function serverErrorText(res: Response): Promise<string | null> {
  try {
    const text = await res.clone().text();
    if (!text) return null;
    let message = text;
    try {
      const parsed = JSON.parse(text) as unknown;
      if (parsed && typeof parsed === 'object') {
        const e = (parsed as { error?: unknown; message?: unknown }).error
          ?? (parsed as { message?: unknown }).message;
        if (typeof e === 'string') message = e;
      }
    } catch { /* not JSON: keep the text */ }
    return message.length > BODY_MAX ? `${message.slice(0, BODY_MAX)}…` : message;
  } catch {
    return null;
  }
}

/**
 * Set by a server path that has already reported its own 5xx (the middleware
 * deadline, `src/lib/supabase/middleware-deadline.ts`), so the same fault is
 * not filed a second time under a route whose handler never ran.
 */
export const REPORTED_BY_SERVER_HEADER = 'X-Mucka-Reported';

/** Decide whether a finished request is one to report. Pure, for the tests. */
export function shouldReport(url: URL, origin: string, res: Pick<Response, 'status' | 'headers'>): boolean {
  if (url.origin !== origin || !url.pathname.startsWith(API_PREFIX)) return false;
  if (res.status < 500 || res.status > 599) return false;
  // The service worker's "there is no network", not a server fault.
  if (res.headers.get('X-Mucka-Offline') === '1') return false;
  if (res.headers.get(REPORTED_BY_SERVER_HEADER) === '1') return false;
  return true;
}

async function observe(input: RequestInfo | URL, init: RequestInit | undefined, res: Response): Promise<void> {
  const rawUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  const url = new URL(rawUrl, window.location.href);
  if (!shouldReport(url, window.location.origin, res)) return;

  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();
  const route = routeTemplate(url.pathname);
  const key = `${method} ${route} ${res.status}`;
  const now = Date.now();
  const last = lastReported.get(key);
  if (last !== undefined && now - last < DEDUPE_MS) return;
  lastReported.set(key, now);

  const serverError = await serverErrorText(res);

  // Route and status in the title so an issue list reads as a list of broken
  // things; the server's sentence in `extra`, because it can carry a value and
  // a value in the title is a new issue per value.
  Sentry.captureMessage(`[api ${res.status}] ${method} ${route}`, {
    level: 'error',
    tags: { area: 'browser-5xx', route, method, status: String(res.status) },
    fingerprint: ['browser-5xx', method, route, String(res.status)],
    extra: {
      serverError,
      pathname: window.location.pathname,
      meaning: 'A user\'s browser got a server error back from our own API. '
        + 'Whatever the screen did with it (usually a red toast), this is the '
        + 'only record of it unless the route reports for itself.',
    },
  });
  console.warn(`[api ${res.status}] ${method} ${route}`, serverError);
}

let installed = false;

export function installServerErrorWatch(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const original = window.fetch;

  window.fetch = function watchedFetch(this: unknown, input: RequestInfo | URL, init?: RequestInit) {
    const pending = original.call(this ?? window, input, init);
    // Detached: the caller gets `pending` itself, and a rejection is still
    // theirs to handle. The second handler only stops THIS branch adding an
    // unhandled rejection of its own.
    pending.then(
      res => { observe(input, init, res).catch(() => {}); },
      () => {},
    );
    return pending;
  } as typeof window.fetch;
}

/** Test seam. */
export function resetServerErrorWatchForTests(): void {
  installed = false;
  lastReported.clear();
}
